import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import useBgPages from '../../../hooks/use-bg-pages.hook';
import Asaid from '../../../components/layout/asaid.component';
import Header from '../../../components/layout/header.component';
import Footer from '../../../components/layout/footer.component';
import Icon from '../../../components/forms/icon.component';
import { AlertService } from '../../../services/alert.service';
import {
  ReportsService,
  downloadBlob,
  type CallItem,
  type CallsPage,
  type CallsQuery,
  type HangupCauseRow,
  type OperatorDailyRow,
  type OperatorHourlyRow,
  type RangeQuery,
  type ReportSummary,
  type RouteDailyRow,
  type RouteHourlyRow
} from '../../../services/reports.service';
import { HangupCausesChart } from '../dashboard/dashboard.charts';
import ReportsFilters from './reports-filters.component';
import CallsTable from './calls-table.component';
import CallDetailModal from './call-detail.modal';
import HourlyChart from './hourly-chart.component';
import DailyTable from './daily-table.component';
import { fmtSecs, num, pctRate, type ReportFilters } from './reports.types';

type Tab = 'calls' | 'daily' | 'hourly' | 'hangup';

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'calls', label: 'Llamadas', icon: 'ki-phone' },
  { key: 'daily', label: 'Por día', icon: 'ki-calendar' },
  { key: 'hourly', label: 'Por hora', icon: 'ki-time' },
  { key: 'hangup', label: 'Hangup causes', icon: 'ki-cross-circle' }
];

const today = moment().format('YYYY-MM-DD');
const DEFAULT_FILTERS: ReportFilters = {
  from: today,
  to: today,
  operatorId: '',
  routeGroupId: '',
  cpdVerdict: '',
  hangupCause: '',
  answered: '',
  billsecMin: '',
  billsecMax: ''
};

/** filtros de pantalla → query del backend (sin llaves vacías). */
function toRange(f: ReportFilters): RangeQuery {
  return {
    from: f.from,
    to: f.to,
    operatorId: f.operatorId.trim() || undefined,
    routeGroupId: f.routeGroupId.trim() === '' ? undefined : Number(f.routeGroupId)
  };
}
function toCalls(f: ReportFilters, limit: number, offset: number): CallsQuery {
  return {
    ...toRange(f),
    cpdVerdict: f.cpdVerdict || undefined,
    hangupCause: f.hangupCause.trim() || undefined,
    answered: f.answered === '' ? undefined : f.answered === 'true',
    billsecMin: f.billsecMin.trim() === '' ? undefined : Number(f.billsecMin),
    billsecMax: f.billsecMax.trim() === '' ? undefined : Number(f.billsecMax),
    limit,
    offset
  };
}

const ReportsPage: React.FC = () => {
  useBgPages();
  const [draft, setDraft] = useState<ReportFilters>(DEFAULT_FILTERS);
  const [applied, setApplied] = useState<ReportFilters>(DEFAULT_FILTERS);
  const [tab, setTab] = useState<Tab>('calls');

  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [calls, setCalls] = useState<CallsPage | null>(null);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [dailyOps, setDailyOps] = useState<OperatorDailyRow[]>([]);
  const [dailyRoutes, setDailyRoutes] = useState<RouteDailyRow[]>([]);
  const [hourlyMode, setHourlyMode] = useState<'operators' | 'routes'>('operators');
  const [hourly, setHourly] = useState<(OperatorHourlyRow | RouteHourlyRow)[]>([]);
  const [hangup, setHangup] = useState<HangupCauseRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'' | 'csv' | 'xlsx'>('');
  const [detail, setDetail] = useState<CallItem | null>(null);

  const fail = (e: any, what: string) =>
    AlertService.toastError(e?.error || e?.status || `No se pudo cargar ${what}`);

  const loadSummary = useCallback(async (f: ReportFilters) => {
    try {
      setSummary(await ReportsService.getSummary(toRange(f)));
    } catch (e) {
      fail(e, 'el resumen');
    }
  }, []);

  const loadCalls = useCallback(async (f: ReportFilters, l: number, o: number) => {
    try {
      setLoading(true);
      setCalls(await ReportsService.getCalls(toCalls(f, l, o)));
    } catch (e) {
      fail(e, 'las llamadas');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTab = useCallback(
    async (t: Tab, f: ReportFilters, mode = hourlyMode) => {
      try {
        setLoading(true);
        const r = toRange(f);
        if (t === 'daily') {
          const [ops, rts] = await Promise.all([
            ReportsService.getDailyByOperator(r),
            ReportsService.getDailyByRoute(r)
          ]);
          setDailyOps(ops);
          setDailyRoutes(rts);
        } else if (t === 'hourly') {
          const hq = { ...r, from: `${f.from}T00:00:00`, to: `${f.to}T23:59:59` };
          setHourly(
            mode === 'operators'
              ? await ReportsService.getHourlyByOperator(hq)
              : await ReportsService.getHourlyByRoute(hq)
          );
        } else if (t === 'hangup') {
          setHangup(await ReportsService.getHangupCauses({ ...r, limit: 25 }));
        }
      } catch (e) {
        fail(e, 'el reporte');
      } finally {
        setLoading(false);
      }
    },
    [hourlyMode]
  );

  const apply = useCallback(() => {
    setApplied(draft);
    setOffset(0);
    loadSummary(draft);
    if (tab === 'calls') loadCalls(draft, limit, 0);
    else loadTab(tab, draft);
  }, [draft, tab, limit, loadSummary, loadCalls, loadTab]);

  // carga inicial
  useEffect(() => {
    loadSummary(DEFAULT_FILTERS);
    loadCalls(DEFAULT_FILTERS, 50, 0);
  }, [loadSummary, loadCalls]);

  const changeTab = (t: Tab) => {
    setTab(t);
    if (t === 'calls') loadCalls(applied, limit, offset);
    else loadTab(t, applied);
  };

  const exportCalls = async (format: 'csv' | 'xlsx') => {
    try {
      setExporting(format);
      const q = toCalls(applied, 0, 0);
      const { limit: _l, offset: _o, ...rest } = q;
      const filename = `calls_${applied.from}_${applied.to}`;
      const blob = await ReportsService.exportCalls({ ...rest, format, filename });
      downloadBlob(blob, `${filename}.${format}`);
      AlertService.toastSuccess(`Export ${format.toUpperCase()} descargado`);
    } catch (e: any) {
      fail(e, 'el export');
    } finally {
      setExporting('');
    }
  };

  const rangeLabel = applied.from === applied.to ? applied.from : `${applied.from} → ${applied.to}`;
  const kpis = useMemo(
    () => [
      { label: 'Llamadas', value: num(summary?.total), color: 'primary' },
      { label: 'Contestadas', value: num(summary?.answered), color: 'info' },
      {
        label: 'Humano',
        value: `${num(summary?.human)} · ${pctRate(summary?.humanRate)}`,
        color: 'success'
      },
      {
        label: 'Buzón (603)',
        value: `${num(summary?.voicemail)} · ${pctRate(summary?.voicemailRate)}`,
        color: 'danger'
      },
      { label: 'Sin veredicto', value: num(summary?.unknown), color: 'secondary' },
      { label: 'Bypass', value: num(summary?.bypassed), color: 'primary' },
      { label: 'Facturable', value: fmtSecs(summary?.billsecSum), color: 'dark' }
    ],
    [summary]
  );

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Asaid />
      <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="content fs-6 d-flex flex-column flex-column-fluid bg-cards-p"
          id="kt_content"
        >
          <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
            <div className="container-fluid px-4 px-md-10 px-xxl-15">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-5">
                <div>
                  <h1 className="fw-bolder fs-2x mb-1">Reportes CPD</h1>
                  <div className="text-muted fs-7">
                    Histórico de llamadas desde ClickHouse · {rangeLabel}
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-light-primary fw-bold fs-7"
                    disabled={!!exporting}
                    onClick={() => exportCalls('csv')}
                  >
                    <Icon icon="ki-file-down" size="fs-4 me-1" />
                    {exporting === 'csv' ? 'Exportando…' : 'CSV'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-light-success fw-bold fs-7"
                    disabled={!!exporting}
                    onClick={() => exportCalls('xlsx')}
                  >
                    <Icon icon="ki-file-down" size="fs-4 me-1" />
                    {exporting === 'xlsx' ? 'Exportando…' : 'Excel'}
                  </button>
                </div>
              </div>

              <ReportsFilters value={draft} onChange={setDraft} onApply={apply} loading={loading} />

              <div className="row g-4 mt-2">
                {kpis.map((k) => (
                  <div className="col-6 col-md-4 col-xl" key={k.label}>
                    <div
                      className={`border border-dashed border-gray-300 rounded-3 p-4 h-100 bg-light-${k.color}`}
                    >
                      <div className="fs-8 text-muted fw-semibold text-uppercase">{k.label}</div>
                      <div className={`fs-3 fw-bolder text-${k.color}`}>{k.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="card shadow-sm border-0 rounded-4 mt-6">
                <div className="card-header border-0 pt-5 d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <ul className="nav nav-pills nav-pills-custom gap-2">
                    {TABS.map((t) => (
                      <li className="nav-item" key={t.key}>
                        <button
                          type="button"
                          className={`nav-link btn btn-sm fw-bold fs-7 ${tab === t.key ? 'active btn-primary' : 'btn-light'}`}
                          onClick={() => changeTab(t.key)}
                        >
                          <Icon icon={t.icon} size="fs-5 me-1" /> {t.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                  {tab === 'hourly' && (
                    <div className="btn-group btn-group-sm">
                      <button
                        type="button"
                        className={`btn fw-bold ${hourlyMode === 'operators' ? 'btn-primary' : 'btn-light'}`}
                        onClick={() => {
                          setHourlyMode('operators');
                          loadTab('hourly', applied, 'operators');
                        }}
                      >
                        Por operador
                      </button>
                      <button
                        type="button"
                        className={`btn fw-bold ${hourlyMode === 'routes' ? 'btn-primary' : 'btn-light'}`}
                        onClick={() => {
                          setHourlyMode('routes');
                          loadTab('hourly', applied, 'routes');
                        }}
                      >
                        Por ruta
                      </button>
                    </div>
                  )}
                </div>
                <div className="card-body pt-4">
                  {tab === 'calls' && (
                    <CallsTable
                      page={calls}
                      loading={loading}
                      limit={limit}
                      onLimit={(n) => {
                        setLimit(n);
                        setOffset(0);
                        loadCalls(applied, n, 0);
                      }}
                      onOffset={(o) => {
                        setOffset(o);
                        loadCalls(applied, limit, o);
                      }}
                      onDetail={setDetail}
                    />
                  )}
                  {tab === 'daily' && (
                    <div className="row g-6">
                      <div className="col-xl-6">
                        <div className="fs-7 fw-bold text-gray-600 mb-2">Por operador</div>
                        <DailyTable
                          rows={dailyOps}
                          keyLabel="Operador"
                          keyOf={(r) => (r as OperatorDailyRow).operatorId ?? ''}
                        />
                      </div>
                      <div className="col-xl-6">
                        <div className="fs-7 fw-bold text-gray-600 mb-2">Por grupo de ruta</div>
                        <DailyTable
                          rows={dailyRoutes}
                          keyLabel="Ruta"
                          keyOf={(r) => String((r as RouteDailyRow).routeGroupId ?? '')}
                        />
                      </div>
                    </div>
                  )}
                  {tab === 'hourly' &&
                    (hourly.length ? (
                      <HourlyChart rows={hourly} />
                    ) : (
                      <div className="text-center text-muted py-10">
                        {loading ? 'Consultando…' : 'Sin datos por hora en el rango'}
                      </div>
                    ))}
                  {tab === 'hangup' &&
                    (hangup.length ? (
                      <HangupCausesChart
                        rows={hangup.map((h) => ({ key: h.hangupCause, count: h.count }))}
                        height={Math.max(260, hangup.length * 28)}
                      />
                    ) : (
                      <div className="text-center text-muted py-10">
                        {loading ? 'Consultando…' : 'Sin cuelgues en el rango'}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {detail && (
        <CallDetailModal callUuid={detail.callUuid} show onRequestClose={() => setDetail(null)} />
      )}
    </div>
  );
};

export default ReportsPage;
